import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, List, ListItem, Typography } from '@mui/material'
import { createElement } from 'react'
import { type ItemValue, ElementFactory, Question, Serializer } from 'survey-core'
import { ReactQuestionFactory, SurveyQuestionElementBase } from 'survey-react-ui'

const CUSTOM_TYPE = 'surveycheckbox'

export class SurveyCheckboxModel extends Question {
  constructor (name: string) {
    super(name)
    this.titleLocation = 'hidden'
    this.createItemValues('choices')
  }

  getType (): string {
    return CUSTOM_TYPE
  }

  get choices (): ItemValue[] {
    return this.getPropertyValue('choices')
  }

  set choices (val: ItemValue[]) {
    this.setPropertyValue('choices', val)
  }

  get choiceValues (): string[] {
    return this.choices.map((c: ItemValue) => c.value as string)
  }

  get choiceTexts (): Array<{ value: string, text: string }> {
    return this.choices.map((c: ItemValue) => ({
      value: c.value as string,
      text: (c.text ?? c.value)
    }))
  }

  get choiceDetails (): Record<string, string[]> | undefined {
    return this.getPropertyValue('choiceDetails')
  }

  set choiceDetails (val: Record<string, string[]> | undefined) {
    this.setPropertyValue('choiceDetails', val)
  }

  get minSelectedChoices (): number | undefined {
    return this.getPropertyValue('minSelectedChoices')
  }

  set minSelectedChoices (val: number | undefined) {
    this.setPropertyValue('minSelectedChoices', val)
  }

  isEmpty (): boolean {
    const val = this.value
    if (val == null || !Array.isArray(val) || val.length === 0) return true
    if (this.minSelectedChoices != null && val.length < this.minSelectedChoices) return true
    return false
  }
}

export function registerCheckboxQuestion (): void {
  ElementFactory.Instance.registerElement(
    CUSTOM_TYPE,
    (name) => {
      return new SurveyCheckboxModel(name)
    }
  )

  Serializer.addClass(
    CUSTOM_TYPE,
    [{
      name: 'choices',
      type: 'itemvalue[]',
      category: 'general'
    }, {
      name: 'choiceDetails',
      type: 'object',
      category: 'general'
    }, {
      name: 'minSelectedChoices',
      type: 'number',
      category: 'general'
    }],
    function () {
      return new SurveyCheckboxModel('')
    },
    'question'
  )

  ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
    return createElement(SurveyQuestionCheckbox, props)
  })
}

export class SurveyQuestionCheckbox extends SurveyQuestionElementBase {
  get question (): SurveyCheckboxModel {
    return this.questionBase as SurveyCheckboxModel
  }

  handleChange = (choiceValue: string, checked: boolean): void => {
    const current: string[] = Array.isArray(this.question.value) ? [...this.question.value] : []
    if (checked) {
      if (!current.includes(choiceValue)) {
        current.push(choiceValue)
      }
    } else {
      const index = current.indexOf(choiceValue)
      if (index > -1) {
        current.splice(index, 1)
      }
    }
    this.question.value = current.length > 0 ? current : undefined
  }

  renderElement (): JSX.Element {
    const selectedValues: string[] = Array.isArray(this.question.value) ? this.question.value : []

    return (
      <FormControl component="fieldset">
        <FormLabel>
          {this.question.fullTitle}
        </FormLabel>
        {this.question.description != null && this.question.description !== '' && (
          <Typography sx={{ width: '80%', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginY: '10px' }}>
            {this.question.description}
          </Typography>
        )}
        <FormGroup data-testid={this.question.name}>
          {this.question.choiceTexts.map((choice) => {
            const subItems = this.question.choiceDetails?.[choice.value]
            return (
              <Box key={choice.value}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedValues.includes(choice.value)}
                      onChange={(e) => { this.handleChange(choice.value, e.target.checked) }}
                      data-testid={`${this.question.name}-${choice.value}`}
                    />
                  }
                  label={choice.text}
                />
                {subItems != null && subItems.length > 0 && (
                  <List sx={{ listStyleType: 'disc', pl: 7, mt: 0, mb: 1 }}>
                    {subItems.map((item, index) => (
                      <ListItem key={index} sx={{ display: 'list-item', p: 0 }}>
                        <Typography variant="body2">{item}</Typography>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )
          })}
        </FormGroup>
      </FormControl>
    )
  }
}
